import { useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import API from "../../../../api";
import { colors } from "../../../../theme";
import IntelligenceTimeFilter from "../../../../components/IntelligenceTimeFilter";
import { CustomBarList } from "../../../../components/dataGridWithBarGraphOnRight";
import LogsChart from "./logsChart";

dayjs.extend(utc);
dayjs.extend(timezone);

// "Network Issues" tab — network problems (left) and the traffic timeline moved
// in from Connections (right), both already IP-scoped server-side, driven by one
// shared time filter across the top (same style as the security grids).
const NetworkIssuesPanel = ({ ip }) => {
  const tz = sessionStorage.getItem("timezone") || "Etc/GMT";
  const [dateFilter, setDateFilter] = useState({
    dateFrom: dayjs().tz(tz).subtract(1, "day"),
    dateTo: dayjs().tz(tz),
  });
  const [categories, setCategories] = useState([]);
  const [seriesData, setSeriesData] = useState([]);
  const [chargedGraph, setChargedGraph] = useState(false);
  // LogsChart fetches its own data internally, so it reports back whether it
  // ended up with anything to plot. Starts `true` so the box stays visible
  // (with its own loading state) until that first report comes in, rather than
  // flashing away and back.
  const [hasLogsData, setHasLogsData] = useState(true);

  const handleChangeDate = (newDateFilter) => {
    setDateFilter({
      dateFrom: dayjs(newDateFilter.dateFrom).tz(tz),
      dateTo: dayjs(newDateFilter.dateTo).tz(tz),
    });
  };

  useEffect(() => {
    if (!ip) return;
    setChargedGraph(false);
    const abortController = new AbortController();
    API.getAnalysisDetail(
      { ip, dateFrom: dateFilter.dateFrom.utc().format(), dateTo: dateFilter.dateTo.utc().format() },
      abortController.signal
    )
      .then((response) => {
        if (response.data && Array.isArray(response.data)) {
          setSeriesData(response.data.map((item) => ({ count: item.count, dettaglio: item.dettaglio })));
          setCategories(response.data.map((item) => item.dettaglio));
        }
        setChargedGraph(true);
      })
      .catch((error) => console.error("[NetworkIssuesPanel] Error fetching network issues:", error));
    return () => abortController.abort();
  }, [ip, dateFilter]);

  const showIssues = !chargedGraph || seriesData.length > 0;
  const showLogs = hasLogsData;

  return (
    <Box sx={{ p: "1rem" }}>
      <IntelligenceTimeFilter
        onDateChange={handleChangeDate}
        initialDateFrom={dateFilter.dateFrom}
        initialDateTo={dateFilter.dateTo}
      />

      {/* Independent boxes, independent data sources — each can end up empty on
          its own. Both stay mounted at all times (LogsChart fetches on its own
          [ip, dateFilter] effect and must keep running even while hidden, or a
          filter change that would bring data back could never be noticed);
          only their visibility and Grid width react to whether they have
          anything to show, so the remaining box takes the full row instead of
          leaving half of it blank. */}
      {!showIssues && !showLogs && (
        <Typography sx={{ color: colors.grey[600], fontSize: 12.5, mt: 2 }}>
          No network issues or events for this asset in the selected time range.
        </Typography>
      )}
      <Grid container spacing={2} sx={{ mt: 0.5, display: !showIssues && !showLogs ? "none" : undefined }}>
        <Grid size={showLogs ? 6 : 12} sx={{ display: showIssues ? undefined : "none" }}>
          <Box sx={{ borderRadius: 1.5, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`, p: 1.5, height: "100%" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#459095", textTransform: "uppercase", letterSpacing: 0.8, mb: 1 }}>
              Network Issues
            </Typography>
            {chargedGraph ? (
              <CustomBarList seriesData={seriesData} categories={categories} sourceIp={ip} />
            ) : (
              <Typography sx={{ color: colors.grey[600], fontSize: 12.5 }}>Loading…</Typography>
            )}
          </Box>
        </Grid>
        <Grid size={showIssues ? 6 : 12} sx={{ display: showLogs ? undefined : "none" }}>
          <LogsChart dateFilter={dateFilter} ip={ip} onDataStateChange={setHasLogsData} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default NetworkIssuesPanel;
